import { useCallback, useRef, useState } from "react";

type Filters = Record<string, unknown>;

type HookValue<F extends Filters> = {
    currentActiveKey: keyof Readonly<F> | null;
    setNextActiveKey: () => void;
};

function* activeFilterGenerator<F extends Filters>(filters: Readonly<F> | undefined): Generator<keyof F> {
    if (typeof filters === "undefined") {return;}

    for (const filterKey in filters) {
        if (filters.hasOwnProperty(filterKey) && !filters[filterKey]) {
            yield filterKey;
        }
    }
}

const useActiveFilterGenerator = <F extends Filters>(filters: Readonly<F> | undefined): HookValue<F> => {
    const { current: generator } = useRef(activeFilterGenerator(filters));

    const [currentActiveKey, setCurrentActiveKey] = useState<keyof F | null>(() => {
        const { value, done } = generator.next();

        return done ? null : value;
    });

    const setNextActiveKey = useCallback(() => {
        const { value, done } = generator.next();

        setCurrentActiveKey(done ? null : value);
    }, []);

    return { currentActiveKey, setNextActiveKey };
};

export default useActiveFilterGenerator;
